import { createSlice, createSelector, PayloadAction } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';

import {
  RootState,
  PathNode,
  PathEdge,
  PathNetwork,
  hasMapResponse,
  MapResponseAction,
} from '../../store';

import {
  activeImageOrDuplicateOfSelector,
  overlaysSelector,
  spacePositionsSelector,
  placementIdToSpaceIdSelector,
  removeOverlayInImage,
} from './images';
import { labelText } from '../../utils/labelText';
import { polygonCentroid } from '../../utils/overlays';
import { spacesSelector } from './spaces';
import { overlayLabelsSelector } from './labels';
import {
  findShortestPath as findPath,
  PathResult,
} from '../../utils/pathfinding';

export interface PathsState {
  nodes: PathNode[];
  edges: PathEdge[];
  drawingFromNodeId: string | null;
  selectedEdgeId: string | null;
  selectedNodeId: string | null;
  customRouteStart: string | null; // spaceId
  customRouteEnd: string | null; // spaceId
  customRouteFromWaypoint: string | null; // nodeId (waypoint) - for QR code deep linking
  showCustomRoute: boolean;
  waypointPickerDismissed: boolean; // true when user clicks "Done" to dismiss picker but keep route
  needsSave: boolean;
}

const initialState: PathsState = {
  nodes: [],
  edges: [],
  drawingFromNodeId: null,
  selectedEdgeId: null,
  selectedNodeId: null,
  customRouteStart: null,
  customRouteEnd: null,
  customRouteFromWaypoint: null,
  showCustomRoute: false,
  waypointPickerDismissed: false,
  needsSave: false,
};

export const pathsSlice = createSlice({
  name: 'paths',
  initialState,
  reducers: {
    // Node management
    addNode: (state, { payload }: PayloadAction<Omit<PathNode, 'id'> & { id?: string }>) => {
      const id = payload.id || uuidv4();
      state.nodes.push({ ...payload, id });
      state.needsSave = true;
    },

    removeNode: (state, { payload: nodeId }: PayloadAction<string>) => {
      state.nodes = state.nodes.filter((n) => n.id !== nodeId);
      // Also remove all edges connected to this node
      state.edges = state.edges.filter((e) => e.from !== nodeId && e.to !== nodeId);
      // Prevent creating a path from a deleted node
      if (state.drawingFromNodeId === nodeId) state.drawingFromNodeId = null;
      state.needsSave = true;
    },

    updateNode: (state, { payload }: PayloadAction<PathNode>) => {
      const index = state.nodes.findIndex((n) => n.id === payload.id);
      if (index !== -1) {
        state.nodes[index] = payload;
        state.needsSave = true;
      }
    },

    moveNode: (state, { payload }: PayloadAction<{ nodeId: string; position: { x: number; y: number } }>) => {
      const index = state.nodes.findIndex((n) => n.id === payload.nodeId);
      if (index !== -1) {
        state.nodes[index] = { ...state.nodes[index], position: payload.position };
        state.needsSave = true;
      }
    },

    removeNodeAndReconnect: (state, { payload: nodeId }: PayloadAction<string>) => {
      // Find all edges connected to this node
      const connectedEdges = state.edges.filter((e) => e.from === nodeId || e.to === nodeId);

      // Collect all unique neighbor node IDs
      const neighborIds = new Set<string>();
      for (const edge of connectedEdges) {
        if (edge.from === nodeId) neighborIds.add(edge.to);
        if (edge.to === nodeId) neighborIds.add(edge.from);
      }

      // Remove the node and its edges
      state.nodes = state.nodes.filter((n) => n.id !== nodeId);
      state.edges = state.edges.filter((e) => e.from !== nodeId && e.to !== nodeId);

      // Reconnect all neighbor pairs
      const neighbors = Array.from(neighborIds);
      for (let i = 0; i < neighbors.length; i++) {
        for (let j = i + 1; j < neighbors.length; j++) {
          const a = neighbors[i];
          const b = neighbors[j];
          // Don't add if edge already exists
          const exists = state.edges.some(
            (e) => (e.from === a && e.to === b) || (e.from === b && e.to === a)
          );
          if (!exists) {
            state.edges.push({
              id: uuidv4(),
              from: a,
              to: b,
              bidirectional: true,
            });
          }
        }
      }

      // Clear selection/drawing state if it referenced this node
      if (state.selectedNodeId === nodeId) state.selectedNodeId = null;
      if (state.drawingFromNodeId === nodeId) state.drawingFromNodeId = null;

      state.needsSave = true;
    },

    // Edge management
    addEdge: (state, { payload }: PayloadAction<Omit<PathEdge, 'id'>>) => {
      // Don't add duplicate edges
      const exists = state.edges.some(
        (e) => (e.from === payload.from && e.to === payload.to) ||
               (payload.bidirectional && e.from === payload.to && e.to === payload.from)
      );
      if (!exists) {
        const id = uuidv4();
        state.edges.push({ ...payload, id });
        state.needsSave = true;
      }
    },

    removeEdge: (state, { payload: edgeId }: PayloadAction<string>) => {
      state.edges = state.edges.filter((e) => e.id !== edgeId);
      state.needsSave = true;
    },

    updateEdge: (state, { payload }: PayloadAction<PathEdge>) => {
      const index = state.edges.findIndex((e) => e.id === payload.id);
      if (index !== -1) {
        state.edges[index] = payload;
        state.needsSave = true;
      }
    },

    setDrawingFromNodeId: (state, { payload }: PayloadAction<string | null>) => {
      state.drawingFromNodeId = payload;
    },

    setSelectedEdgeId: (state, { payload }: PayloadAction<string | null>) => {
      state.selectedEdgeId = payload;
      // Deselect node when selecting edge
      if (payload) state.selectedNodeId = null;
    },

    setSelectedNodeId: (state, { payload }: PayloadAction<string | null>) => {
      state.selectedNodeId = payload;
      // Deselect edge when selecting node
      if (payload) state.selectedEdgeId = null;
    },

    // Custom route selection
    setCustomRouteStart: (state, { payload }: PayloadAction<string | null>) => {
      state.customRouteStart = payload;
    },

    setCustomRouteEnd: (state, { payload }: PayloadAction<string | null>) => {
      state.customRouteEnd = payload;
    },

    setShowCustomRoute: (state, { payload }: PayloadAction<boolean>) => {
      state.showCustomRoute = payload;
    },

    // For QR code deep linking - set a waypoint as the route start
    setCustomRouteFromWaypoint: (state, { payload }: PayloadAction<string | null>) => {
      state.customRouteFromWaypoint = payload;
      // Clear space-based start when using waypoint
      if (payload) {
        state.customRouteStart = null;
        state.waypointPickerDismissed = false;
      }
    },

    // Dismiss the waypoint picker modal but keep the route visible
    dismissWaypointPicker: (state) => {
      state.waypointPickerDismissed = true;
    },

    clearCustomRoute: (state) => {
      state.customRouteStart = null;
      state.customRouteEnd = null;
      state.customRouteFromWaypoint = null;
      state.showCustomRoute = false;
      state.waypointPickerDismissed = false;
    },

    // Bulk operations for API loading
    setPathNetwork: (state, { payload }: PayloadAction<{ nodes: PathNode[]; edges: PathEdge[] }>) => {
      state.nodes = payload.nodes;
      state.edges = payload.edges;
    },

    clearAllPaths: (state) => {
      state.nodes = [];
      state.edges = [];
      state.drawingFromNodeId = null;
      state.selectedEdgeId = null;
      state.selectedNodeId = null;
      state.needsSave = true;
    },
  },
  extraReducers: (builder) => {
    // Nodes of a removed overlay become plain waypoints
    builder.addCase(removeOverlayInImage, (state, { payload: overlayId }) => {
      state.nodes.forEach((node) => {
        if (node.overlayId !== overlayId) return;
        delete node.overlayId;
        state.needsSave = true;
      });
    });
    // Load paths from API response
    builder.addMatcher(
      (action): action is MapResponseAction => hasMapResponse(action),
      (state, { payload }) => {
        if (payload.paths) {
          state.nodes = payload.paths.nodes || [];
          state.edges = payload.paths.edges || [];
        } else {
          // No paths in response, keep empty
          state.nodes = [];
          state.edges = [];
        }
        // Reset UI state on load/save
        state.drawingFromNodeId = null;
        state.selectedEdgeId = null;
        state.selectedNodeId = null;
        state.customRouteStart = null;
        state.customRouteEnd = null;
        state.showCustomRoute = false;
        state.needsSave = false;
      }
    );
  },
});

export const {
  addNode,
  removeNode,
  updateNode,
  moveNode,
  removeNodeAndReconnect,
  addEdge,
  removeEdge,
  updateEdge,
  setDrawingFromNodeId,
  setSelectedEdgeId,
  setSelectedNodeId,
  setCustomRouteStart,
  setCustomRouteEnd,
  setShowCustomRoute,
  setCustomRouteFromWaypoint,
  dismissWaypointPicker,
  clearCustomRoute,
  setPathNetwork,
  clearAllPaths,
} = pathsSlice.actions;

// Selectors
export const pathNodesSelector = (state: RootState) => state.paths.nodes;
export const pathEdgesSelector = (state: RootState) => state.paths.edges;
export const drawingFromNodeIdSelector = (state: RootState) => state.paths.drawingFromNodeId;
export const selectedEdgeIdSelector = (state: RootState) => state.paths.selectedEdgeId;
export const selectedNodeIdSelector = (state: RootState) => state.paths.selectedNodeId;
export const customRouteStartSelector = (state: RootState) => state.paths.customRouteStart;
export const customRouteEndSelector = (state: RootState) => state.paths.customRouteEnd;
export const customRouteFromWaypointSelector = (state: RootState) => state.paths.customRouteFromWaypoint;
export const showCustomRouteSelector = (state: RootState) => state.paths.showCustomRoute;
export const waypointPickerDismissedSelector = (state: RootState) => state.paths.waypointPickerDismissed;
export const pathsNeedsSaveSelector = (state: RootState) => state.paths.needsSave;

// Get the full path network
export const pathNetworkSelector = createSelector(
  pathNodesSelector,
  pathEdgesSelector,
  overlaysSelector,
  (nodes, edges, overlays): PathNetwork => {
    if (!nodes.some((node) => node.overlayId)) return { nodes, edges };

    // Nodes linked to an overlay sit at its centroid
    const overlayCentroids = new Map(overlays.map((overlay) => [overlay.id, polygonCentroid(overlay.points)]));
    return {
      nodes: nodes.map((node) => {
        const centroid = node.overlayId ? overlayCentroids.get(node.overlayId) : undefined;
        return centroid ? { ...node, position: centroid } : node;
      }),
      edges,
    };
  }
);

// Get nodes for the current layer only
export const currentLayerNodesSelector = createSelector(
  pathNetworkSelector,
  activeImageOrDuplicateOfSelector,
  (network, activeImage) => {
    if (!activeImage) return [];
    return network.nodes.filter((n) => n.layerId === activeImage.id);
  }
);

// Get edges for the current layer only
export const currentLayerEdgesSelector = createSelector(
  pathEdgesSelector,
  currentLayerNodesSelector,
  (edges, layerNodes) => {
    const nodeIds = new Set(layerNodes.map((n) => n.id));
    return edges.filter((e) => nodeIds.has(e.from) && nodeIds.has(e.to));
  }
);

// Get path segments for rendering on current layer
// For nodes linked to spaces, use the space's actual position (not the stored node position)
export const currentLayerPathSegmentsSelector = createSelector(
  pathNetworkSelector,
  activeImageOrDuplicateOfSelector,
  spacePositionsSelector,
  placementIdToSpaceIdSelector,
  (network, activeImage, spacePositions, placementIdToSpaceId) => {
    if (!activeImage) return [];

    // Create a position lookup that uses space positions for space-linked nodes
    // Handle direct space IDs, placement UUIDs, or fallback to position matching
    const getNodePosition = (node: PathNode): { x: number; y: number } => {
      if (node.spaceId) {
        const spaceIdStr = String(node.spaceId);
        // First try direct lookup
        if (spacePositions[spaceIdStr]) {
          return { x: spacePositions[spaceIdStr].x, y: spacePositions[spaceIdStr].y };
        }
        // If not found, check if it's a placement UUID and map it
        const mappedSpaceId = placementIdToSpaceId[spaceIdStr];
        if (mappedSpaceId && spacePositions[mappedSpaceId]) {
          return { x: spacePositions[mappedSpaceId].x, y: spacePositions[mappedSpaceId].y };
        }
        // Fallback: if the node has a stored position near a space, use that space's position
        // This handles the case where backend transforms spaceId to unknown UUID
        // Use tight threshold - only for nearly exact position matches
        const threshold = 0.01;
        for (const [, pos] of Object.entries(spacePositions)) {
          if (Math.abs(pos.x - node.position.x) < threshold &&
              Math.abs(pos.y - node.position.y) < threshold) {
            return { x: pos.x, y: pos.y };
          }
        }
      }
      return node.position;
    };

    // Custom segment generation using space positions
    const nodeMap = new Map(network.nodes.map((n) => [n.id, n]));
    const segments: Array<{ from: { x: number; y: number }; to: { x: number; y: number }; edgeId: string }> = [];

    for (const edge of network.edges) {
      const fromNode = nodeMap.get(edge.from);
      const toNode = nodeMap.get(edge.to);

      if (!fromNode || !toNode) continue;

      // Filter by layer
      if (fromNode.layerId !== activeImage.id || toNode.layerId !== activeImage.id) {
        continue;
      }

      segments.push({
        from: getNodePosition(fromNode),
        to: getNodePosition(toNode),
        edgeId: edge.id,
      });
    }

    return segments;
  }
);

// Get node by spaceId for the current layer
export const getNodeBySpaceIdSelector = createSelector(
  currentLayerNodesSelector,
  (nodes) => (spaceId: string) => nodes.find((n) => n.spaceId && String(n.spaceId) === String(spaceId))
);

// Check if a space already has a node in the current layer
export const spaceHasNodeSelector = createSelector(
  currentLayerNodesSelector,
  (nodes) => (spaceId: string) => nodes.some((n) => n.spaceId && String(n.spaceId) === String(spaceId))
);

// Get spaces that can be connected (have nodes in current layer)
export const connectableSpacesSelector = createSelector(
  currentLayerNodesSelector,
  spacesSelector,
  (nodes, spaces) => {
    // Convert spaceIds to strings for consistent comparison
    const spaceIds = new Set(nodes.map((n) => n.spaceId ? String(n.spaceId) : null).filter(Boolean));
    return spaces.filter((s) => spaceIds.has(String(s.id)));
  }
);

// Helper to fix route positions - use space positions for space-linked nodes
// Handles direct space IDs, placement UUIDs, and position-based fallback
const fixRoutePositions = (
  result: PathResult | null,
  network: PathNetwork,
  spacePositions: Record<string, { x: number; y: number; layerId: number }>,
  placementIdToSpaceId: Record<string, string> = {}
): PathResult | null => {
  if (!result?.found || !result.positions) return result;

  const nodeMap = new Map(network.nodes.map((n) => [n.id, n]));

  const fixedPositions = result.path.map((nodeId) => {
    const node = nodeMap.get(nodeId);
    if (node?.spaceId) {
      const spaceIdStr = String(node.spaceId);
      // Direct lookup
      if (spacePositions[spaceIdStr]) {
        return { x: spacePositions[spaceIdStr].x, y: spacePositions[spaceIdStr].y };
      }
      // Fallback: check if it's a placement UUID and map it
      const mappedSpaceId = placementIdToSpaceId[spaceIdStr];
      if (mappedSpaceId && spacePositions[mappedSpaceId]) {
        return { x: spacePositions[mappedSpaceId].x, y: spacePositions[mappedSpaceId].y };
      }
      // Final fallback: find space by position proximity (tight threshold)
      const threshold = 0.01;
      for (const pos of Object.values(spacePositions)) {
        if (Math.abs(pos.x - node.position.x) < threshold &&
            Math.abs(pos.y - node.position.y) < threshold) {
          return { x: pos.x, y: pos.y };
        }
      }
    }
    return node?.position || { x: 0, y: 0 };
  });

  return { ...result, positions: fixedPositions };
};

// Route endpoints are space ids, or prefixed ids of overlays without space
const OVERLAY_ENDPOINT_PREFIX = 'overlay:';

// Path node of a route endpoint on a layer
const findEndpointNode = (network: PathNetwork, endpointId: string, layerId: number) => {
  const overlayId = endpointId.startsWith(OVERLAY_ENDPOINT_PREFIX)
    ? endpointId.slice(OVERLAY_ENDPOINT_PREFIX.length)
    : null;
  return network.nodes.find((n) => n.layerId === layerId && (overlayId
    ? n.overlayId === overlayId
    : !!n.spaceId && String(n.spaceId) === String(endpointId)));
};

// Calculate the custom route between start and end
export const customRouteResultSelector = createSelector(
  pathNetworkSelector,
  customRouteStartSelector,
  customRouteEndSelector,
  activeImageOrDuplicateOfSelector,
  spacePositionsSelector,
  placementIdToSpaceIdSelector,
  (network, startSpaceId, endSpaceId, activeImage, spacePositions, placementIdToSpaceId): PathResult | null => {
    if (!startSpaceId || !endSpaceId || !activeImage) return null;

    const startNode = findEndpointNode(network, startSpaceId, activeImage.id);
    const endNode = findEndpointNode(network, endSpaceId, activeImage.id);
    if (!startNode || !endNode) return { found: false, path: [], distance: Infinity };

    const result = findPath(network, startNode.id, endNode.id, {
      layerId: activeImage.id,
    });

    return fixRoutePositions(result, network, spacePositions, placementIdToSpaceId);
  }
);

// Calculate route from a waypoint to a space (for QR code deep linking)
export const waypointRouteResultSelector = createSelector(
  pathNetworkSelector,
  customRouteFromWaypointSelector,
  customRouteEndSelector,
  activeImageOrDuplicateOfSelector,
  spacePositionsSelector,
  placementIdToSpaceIdSelector,
  (network, fromWaypointId, endSpaceId, activeImage, spacePositions, placementIdToSpaceId): PathResult | null => {
    if (!fromWaypointId || !endSpaceId || !activeImage) return null;

    const endNode = findEndpointNode(network, endSpaceId, activeImage.id);
    if (!endNode) return null;

    // Route from waypoint node directly to end node
    const result = findPath(network, fromWaypointId, endNode.id, {
      layerId: activeImage.id,
    });

    return fixRoutePositions(result, network, spacePositions, placementIdToSpaceId);
  }
);

// Get current route result
export const routeResultSelector = (state: RootState) => {
  const fromWaypointId = customRouteFromWaypointSelector(state);
  return fromWaypointId
    ? waypointRouteResultSelector(state)
    : customRouteResultSelector(state);
};

// Get the starting waypoint node (for displaying its name in the picker)
export const fromWaypointNodeSelector = createSelector(
  pathNodesSelector,
  customRouteFromWaypointSelector,
  activeImageOrDuplicateOfSelector,
  (nodes, waypointId, activeImage): PathNode | null => {
    if (!waypointId) return null;
    return nodes.find((n) => n.id === waypointId && n.layerId === activeImage?.id) || null;
  }
);

export interface RouteEndpoint {
  id: string;
  name: string;
}

// Possible route start/end points: spaces placed on the current layer, and
// labelled overlays without space, connected to the path network
export const routeEndpointsSelector = createSelector(
  connectableSpacesSelector,
  spacePositionsSelector,
  activeImageOrDuplicateOfSelector,
  currentLayerNodesSelector,
  overlaysSelector,
  overlayLabelsSelector,
  (spaces, positions, activeImage, nodes, overlays, overlayLabels): RouteEndpoint[] => {
    if (!activeImage) return [];
    // Only include spaces that are placed on the current layer
    // Convert space id to string for consistent lookup
    const spaceEndpoints = spaces.filter((s) => {
      const pos = positions[String(s.id)];
      return pos && pos.layerId === activeImage.id;
    }).map(({ id, name }) => ({ id: String(id), name }));

    const overlayNodeIds = new Set(nodes.map((n) => n.overlayId).filter(Boolean));
    const labelNames = new Map(overlayLabels.map((label) => [label.id, labelText(label.name)]));
    const overlayEndpoints = overlays.flatMap((overlay) => {
      const name = overlay.labelId && labelNames.get(overlay.labelId);
      if (overlay.spaceId || !name || !overlayNodeIds.has(overlay.id)) return [];
      return [{ id: `${OVERLAY_ENDPOINT_PREFIX}${overlay.id}`, name }];
    });

    return [...spaceEndpoints, ...overlayEndpoints].sort((a, b) => a.name.localeCompare(b.name));
  }
);

// Check if there are any usable paths on the current layer
export const hasPathsOnCurrentLayerSelector = createSelector(
  currentLayerEdgesSelector,
  (edges) => edges.length > 0
);

export default pathsSlice.reducer;
